import React, { useCallback, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { createUserComment } from '@shared/helpers/comment.helpers';
import { IElementID } from '@shared/models/project.model';
import { getProject } from '@client/store/project';
import { CommentInput } from './CommentInput';
import UserAvatar from '@features/users/UserAvatar';
import { AMP_APP_NODON_AI, NEW_COMMENT_ID_PREFIX } from '@shared/constants';
import { gridItemLeftStyles, gridItemRightStyles } from './CommentRow.styles';
import { useUser } from '@client/hooks/use-user.hooks';
import { v4 as uuid } from 'uuid';
import amplitudeLog from '@client/amplitude';

const useTmpUUID = () => {
  const [tmpId, setTmpId] = useState<string>(() => uuid());
  return useMemo(
    () => ({ id: tmpId, rotateId: () => setTmpId(uuid()) }),
    [tmpId],
  );
};

const NewComment: React.FC<{ elementId: IElementID }> = ({ elementId }) => {
  const project = getProject();
  const user = useUser();
  const createInDB = useCommentsStore(({ createComment }) => createComment);

  const componentId = NEW_COMMENT_ID_PREFIX + '-' + elementId;

  const tmpId = useTmpUUID();

  const create = useCallback(
    (message: string) => {
      if (!user) return;

      const comment = createUserComment({
        project_id: project.id,
        owner: user.id,
        message,
        element_id: elementId,
      });

      amplitudeLog(AMP_APP_NODON_AI, {
        ElementId: elementId,
        Message: message,
      });
      createInDB(comment, componentId);
      tmpId.rotateId();
    },
    [createInDB, elementId, componentId, project.id, user, tmpId],
  );

  return (
    <Grid container>
      <Grid {...gridItemLeftStyles}>
        <UserAvatar small mt={1.5} mr={1.5} />
      </Grid>
      <Grid {...gridItemRightStyles}>
        <CommentInput
          key={tmpId.id} // reset this when input is saved
          id={componentId}
          message=""
          onSave={create}
        />
      </Grid>
    </Grid>
  );
};

export default NewComment;
